import React, { useEffect, useRef, useState } from "react";
import { staffApi } from "@/lib/staffApi";
import { RemainingWhatsApp } from './RemainingWhatsApp';

const button = "rounded-full border border-gold px-5 py-3 text-sm font-medium disabled:opacity-40";
const channels = [["email", "Email"], ["whatsapp", "WhatsApp"]];

export function ServiceUpdate() {
    const [data, setData] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [running, setRunning] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [selected, setSelected] = useState(["email", "whatsapp"]);
    const [notice, setNotice] = useState("");
    const [error, setError] = useState("");
    const stop = useRef(true);
    const mounted = useRef(true);

    const load = async () => {
        const result = await staffApi.serviceUpdate();
        if (mounted.current) setData(result);
        return result;
    };
    useEffect(() => {
        mounted.current = true;
        load().catch(e => setError(e.message || "Could not load the service update."));
        return () => { mounted.current = false; stop.current = true; };
    }, []);

    async function action(fn) {
        setBusy(true); setError(""); setNotice("");
        try { await fn(); }
        catch (e) { setError(e.message || "The action could not be completed. Refresh before trying again."); }
        finally { if (mounted.current) setBusy(false); }
    }

    async function batches() {
        stop.current = false;
        setRunning(true);
        try {
            while (!stop.current) {
                const result = await staffApi.serviceUpdate("batch");
                if (!mounted.current) return;
                setData(old => ({...old, ...result}));
                if (result.state === "finished") {
                    setNotice("Send run finished. Check the totals below for any messages needing review.");
                    break;
                }
            }
        } finally {
            if (mounted.current) setRunning(false);
        }
    }

    const started = ["preparing", "sending", "finished"].includes(data?.state);
    const totals = preview?.totals || data?.totals;
    const ready = selected.length > 0 && (!selected.includes("email") || data?.email_configured)
        && (!selected.includes("whatsapp") || (data?.meta?.ready && data?.whatsapp_configured));
    const selectedTotal = selected.reduce((sum, channel) => sum + (totals?.[channel]?.total || 0), 0);
    const sendLabel = selected.length === 2 ? "Send now on both channels" : selected.includes("email") ? "Send emails now" : "Send WhatsApp messages now";
    return <section className="glass rounded-3xl border border-gold/30 p-5 sm:p-7 space-y-6 text-white" data-testid="service-update">
        <header>
            <h2 className="font-serif text-3xl text-white">Booking system change</h2>
            <p className="mt-2 text-sm text-white/75">A one-off service update for existing customers, by email and WhatsApp. You choose when to send.</p>
        </header>
        {error && <p role="alert" className="sticky top-0 z-10 rounded-xl bg-red-950 text-white p-4">{error}</p>}
        {notice && <p role="status" className="rounded-xl bg-gold/20 p-4">{notice}</p>}
        {!data ? <p>Loading…</p> : <>
            <div className="rounded-2xl bg-white/5 border border-white/15 p-4 space-y-3">
                <p>WhatsApp approval: <strong>{data.meta.status.replaceAll("_", " ")}</strong></p>
                {data.meta.category && <p className="text-sm">Meta category: {data.meta.category}</p>}
                {!data.meta.ready && <p className="text-sm text-white/75">WhatsApp becomes available when Meta approves this exact message as a service update (Utility). Email can be sent separately. Approval never sends it automatically.</p>}
                {data.meta.category && data.meta.category !== "UTILITY" && <p role="alert">Meta has classified this differently. Review the template before sending; marketing permissions will not be bypassed.</p>}
                <div className="flex flex-wrap gap-3">
                    <button className={button} disabled={busy} onClick={() => action(load)}>Refresh status</button>
                    {data.meta.status === "NOT_SUBMITTED" && <button className={button + " bg-gold text-[#101a35]"} disabled={busy} onClick={() => action(async () => {
                        await staffApi.serviceUpdate("submit"); await load(); setNotice("Submitted to Meta. No customer messages have been sent.");
                    })}>Submit to Meta</button>}
                </div>
                {!data.email_configured && <p role="alert">Email sending needs configuration.</p>}
                {!data.whatsapp_configured && <p role="alert">WhatsApp sending is not enabled or configured.</p>}
            </div>
            <details className="rounded-2xl border border-white/15 p-4">
                <summary className="cursor-pointer text-gold">View email and WhatsApp message</summary>
                <p className="mt-4 font-medium">Email subject: {data.subject}</p>
                <p className="mt-4 whitespace-pre-wrap break-words text-sm leading-relaxed">{data.body}</p>
                <p className="mt-4 text-sm text-white/65">The same wording is used for both channels. Each message uses the customer’s first name.</p>
            </details>
            <div className="space-y-4">
                <h3 className="text-xl text-white">Recipient totals</h3>
                <p className="text-sm text-white/75">All customer records are checked. This service notice does not require marketing consent. WhatsApp uses recorded messaging permission; channel opt-outs are respected. Duplicate contact details are counted once per channel.</p>
                {!started && <fieldset className="flex flex-wrap gap-5" disabled={busy}>
                    <legend className="mb-2 text-sm text-gold">Send by</legend>
                    {channels.map(([channel, label]) => <label key={channel} className="flex items-center gap-2">
                        <input type="checkbox" checked={selected.includes(channel)} onChange={e => {
                            setSelected(old => e.target.checked ? [...old, channel] : old.filter(c => c !== channel));
                            setPreview(null); setConfirming(false); setNotice("");
                        }} />{label}
                    </label>)}
                </fieldset>}
                {!started && <button className={button} disabled={busy} onClick={() => action(async () => {
                    setPreview(await staffApi.serviceUpdate("preview", {channels:selected})); setConfirming(false);
                    setNotice("Totals refreshed. Nothing has been sent.");
                })}>Refresh recipient totals</button>}
                {totals && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {channels.map(([channel, label]) => <div key={channel} className="rounded-2xl bg-white/5 border border-gold/30 p-4 min-w-0">
                        <h4 className="text-gold">{label}</h4>
                        <p className="text-3xl mt-2">{totals[channel].total} <span className="text-sm">recipients</span></p>
                        <p className="text-sm mt-2">{totals[channel].skipped} skipped records</p>
                        {!!totals[channel].skipped && <details className="mt-2 text-sm">
                            <summary className="cursor-pointer">Why were contacts skipped?</summary>
                            {Object.entries(totals[channel].reasons).map(([reason, count]) => <p key={reason} className="mt-2">{reason}: {count}</p>)}
                        </details>}
                    </div>)}
                </div>}
            </div>
            {started && <div className="rounded-2xl border border-white/15 p-4 space-y-3" aria-live="polite">
                <h3 className="text-xl text-white">Send progress</h3>
                {channels.map(([channel, label]) => {
                    const p = data.progress[channel] || {};
                    const d = data.delivery?.[channel] || {};
                    return <div key={channel} className="space-y-1"><p>{label}: {p.accepted || 0} accepted by provider · {p.pending || 0} waiting · {p.deferred || 0} queued for daily batches · {p.skipped || 0} skipped · {(p.needs_review || 0) + (p.processing || 0)} needing review</p>
                        {data.delivery && <p className="text-sm text-white/75">{(d.delivered || 0) + (d.read || 0)} confirmed delivered · {(d.failed || 0) + (d.bounced || 0)} failed or bounced · {(d.unconfirmed || 0) + (d.sent || 0)} delivery not yet confirmed</p>}</div>;
                })}
                {data.email_recovery?.enabled && <p className="text-sm text-gold">Remaining checked emails send automatically in batches of up to {data.email_recovery.daily_notice_limit || 80} per day, leaving room for appointment emails. Next batch available: {new Date(data.email_recovery.resume_after).toLocaleString('en-GB')}. You can close this page.</p>}
                <p className="text-sm text-white/65">Accepted means the provider received the message. It does not confirm arrival on the customer’s phone or in their inbox. Uncertain deliveries are not automatically sent again.</p>
            </div>}
            {!started && !confirming && <button className={button + " bg-gold text-[#101a35]"} disabled={busy || !ready || !preview?.token || !selectedTotal} onClick={() => setConfirming(true)}>Review send</button>}
            {confirming && !started && <div className="rounded-2xl border border-gold p-4 space-y-4" role="group" aria-label="Confirm sending">
                <p>Send now to <strong>{selected.includes("email") ? totals.email.total : 0} email</strong> and <strong>{selected.includes("whatsapp") ? totals.whatsapp.total : 0} WhatsApp</strong> recipients? This message says appointments and deposits have already been transferred.</p>
                <div className="flex flex-wrap gap-3">
                    <button className={button + " bg-gold text-[#101a35]"} disabled={busy || !ready} onClick={() => action(async () => {
                        const result = await staffApi.serviceUpdate("start", {token:preview.token, confirmation:"SEND"});
                        setData(result); setConfirming(false); await batches();
                    })}>{sendLabel}</button>
                    <button className={button} disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
                </div>
            </div>}
            {["preparing", "sending"].includes(data.state) && !running && <button className={button + " bg-gold text-[#101a35]"} disabled={busy} onClick={() => action(batches)}>Continue sending remaining messages</button>}
            {running && <button className={button} onClick={() => { stop.current = true; setNotice("Pausing after the current small batch finishes."); }}>Pause after this batch</button>}
            {started && data.state !== "finished" && <p className="text-sm text-white/65">Keep this page open while sending. If you leave or lose connection, return here and continue the remaining messages. Messages already claimed will not be sent twice.</p>}
            {data.state === 'finished' && <RemainingWhatsApp />}
            <details className="border-t border-white/15 pt-4 text-sm">
                <summary className="cursor-pointer text-gold">Website and social links for the switch</summary>
                <p className="mt-3">When ready, replace the Fresha booking links on the website, Facebook, Instagram and other profiles with <a className="underline break-all" href="https://book.poutaesthetics.co.uk">https://book.poutaesthetics.co.uk</a>.</p>
                <p className="mt-2">This page does not change those links or send anything automatically.</p>
            </details>
        </>}
    </section>;
}
